/**
 * AI services — an AI's words on a learner's answer, asked of the host (D86).
 *
 * An asset never reaches a model: no endpoint, no credential (invariant 1). It names the service, the
 * item and the input; the host answers — the studio with a rule-based mock, the player through its
 * server and the CET AI hub. Feedback and text only, never a score (invariant 2).
 */

/**
 * The services a host may answer. A new one is an entry here and a branch in each host.
 * `transcribe` turns a spoken answer's recording into text: the text is kept, the recording never.
 */
export const AI_SERVICES = ['openAnswerFeedback', 'conversationReply', 'transcribe'] as const;
export type AiService = (typeof AI_SERVICES)[number];

/** An answer and its task, not a document: the largest `input` a request may carry, serialised. */
export const MAX_AI_INPUT_CHARS = 32_000;

/** The hub's limits on one recording (720 design §C13.3): 5 MB and 60 seconds. */
export const MAX_RECORDING_BYTES = 5 * 1024 * 1024;
export const MAX_RECORDING_SECONDS = 60;

/** asset → host. The host replies with the service's answer, or `null` when it has none. */
export interface AiPayload {
  readonly service: AiService;
  /** The question asked about — the step's id in a widget lesson. */
  readonly itemId: string;
  /** Plain data; for `transcribe`, `{ audio: ArrayBuffer, mime }` — the recording, copied over the port. */
  readonly input: Readonly<Record<string, unknown>>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Across realms: a buffer cloned over a port is not `instanceof` this realm's ArrayBuffer. */
const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  Object.prototype.toString.call(value) === '[object ArrayBuffer]';

export function isAiPayload(value: unknown): value is AiPayload {
  if (!isRecord(value)) return false;
  const { service, itemId, input } = value;
  if (typeof service !== 'string' || !(AI_SERVICES as readonly string[]).includes(service)) return false;
  if (typeof itemId !== 'string' || itemId === '' || itemId.length > 200) return false;
  if (!isRecord(input)) return false;
  if (service === 'transcribe') {
    const { audio, mime } = input;
    return isArrayBuffer(audio) && audio.byteLength > 0 && audio.byteLength <= MAX_RECORDING_BYTES
      && typeof mime === 'string' && mime.length <= 100 && /^audio\//.test(mime);
  }
  try {
    return JSON.stringify(input).length <= MAX_AI_INPUT_CHARS;
  } catch {
    return false;
  }
}
